import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IconAlertCircle, IconArrowLeft, IconArrowRight, IconCheck, IconColorSwatch, IconKeyframes, IconLayoutRows, IconLoader2, IconPalette, IconSparkles, IconTypography, IconX } from '@tabler/icons-react'
import { DEFAULT_PAGE_THEME, normalizeProjectTheme, type ProjectTheme as ThemeProjectTheme } from '@webbuilder/theme'
import { getDemoBlock, getDemoPage, getDemoSite } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { brandPaletteOf, hydrateFromDemo, hydrateFromSite, recipeWithBrand, siteMotionOf, suggestedMotion, themeForRecipe, type CreateForm } from '../lib/preview/compose'
import { saveDemoDesign } from '../lib/demoDesign'
import { unpackSiteDesign, withSiteDesign } from '../lib/siteDesign'
import { WizardTopBar } from '../components/ProjectWorkspace/WizardTopBar'
import { ColorsStep, CombinationStep, FontsStep, MotionStep, StylePresetsStep, comboRecipe } from '../components/CreateWithAiModal/WizardStyleStep'
import { WizardSectionsStep } from '../components/CreateWithAiModal/WizardSectionsStep'
import { PreviewLive } from '../components/CreateWithAiModal/CreateWithAiModal'

/**
 * La demo pública de veltica.com (`/demo`): una página de venta que se
 * rediseña sin cuenta, y que quien la arma se lleva como punto de partida.
 *
 * No es otro editor ni otro dibujante: son los pasos de diseño del asistente
 * de proyecto nuevo —combinación, colores, letra, estilo, animación y otra
 * variante de cada sección— sobre la vista previa de siempre, que pinta el
 * editor con el mismo render que al publicar. Lo que se ve es lo que se crea.
 *
 * Sólo lo que más luce y nada que pueda romper la página: no se escriben
 * textos ni se mueven elementos. Para eso está el editor, una vez dentro.
 *
 * «Empezar con este diseño» guarda el formulario en este navegador
 * (`demoDesign`) y lleva a registrarse; al entrar, el panel abre el asistente
 * con él para ponerle nombre y crearlo (ver `Home`).
 *
 * Dos fuentes: `?source=site` es la portada de veltica.com —la que se está
 * viendo cuando se pulsa «Diseña esta página»—; sin él, la página de venta de
 * la demo, con variantes por sección. Y `?embed=1` cuando se abre en la
 * ventana flotante de veltica.com (`designLauncher` en el worker): cerrar
 * avisa a esa página, y llevarse el diseño sale de la ventana a la de arriba.
 */

type DemoStep = 'combination' | 'colors' | 'fonts' | 'style' | 'motion' | 'sections'

const STEPS: { key: DemoStep; labelKey: string; Icon: typeof IconPalette }[] = [
  { key: 'combination', labelKey: 'cwa.step.combination', Icon: IconColorSwatch },
  { key: 'colors', labelKey: 'cwa.step.colors', Icon: IconPalette },
  { key: 'fonts', labelKey: 'cwa.step.type', Icon: IconTypography },
  { key: 'style', labelKey: 'cwa.step.style', Icon: IconSparkles },
  { key: 'motion', labelKey: 'cwa.step.motion', Icon: IconKeyframes },
  { key: 'sections', labelKey: 'cwa.step.sections', Icon: IconLayoutRows },
]

/** El diseño con el que abre la página: el primero de la lista de estilos. */
const START_STYLE = 'product'

/** La página de véltica, a la que vuelve la marca de la barra. */
const HOME_URL = 'https://veltica.com'

const defaultTheme = normalizeProjectTheme(DEFAULT_PAGE_THEME)

const params = new URLSearchParams(window.location.search)
const FROM_SITE = params.get('source') === 'site'
const EMBEDDED = params.get('embed') === '1' && window.parent !== window
/** La barra a la izquierda de veltica.com, en vez de la ventana del asistente. */
const SPLIT = EMBEDDED && params.get('layout') === 'split'
/** Dónde se estaba leyendo veltica.com, como fracción de su alto. */
const START_SCROLL = Math.min(1, Math.max(0, Number(params.get('y')) || 0))

/** A veltica.com: la página ya se ve; puede quitar la suya de delante. */
function announceShown() {
  if (EMBEDDED) window.parent.postMessage({ type: 'veltica:demo-ready' }, '*')
}

/* Lo elegido y lo escrito en veltica.com, cuando se llega con «Empezar con este
   diseño» por su dirección (ver `lib/siteDesign`). */
const SITE_DESIGN = FROM_SITE ? unpackSiteDesign(params.get('design')) : null

/** Salir: en la ventana flotante, se le pide a veltica.com que la cierre. */
function leave() {
  /* En el modo diseño, cerrar sólo recoge la barra (ver `collapse`). */
  if (EMBEDDED) {
    window.parent.postMessage({ type: 'veltica:demo-close' }, '*')
    return
  }
  window.location.href = HOME_URL
}

export function DemoPage({ hasSession }: { hasSession: boolean }) {
  const { t, language } = useI18n()
  const siteLanguage = language === 'es' ? 'es' : 'en'
  const [form, setForm] = useState<CreateForm | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [step, setStep] = useState<DemoStep>('combination')
  const [motionReplay, setMotionReplay] = useState(0)
  const [previewSection, setPreviewSection] = useState<string | null>(null)
  const [reveal, setReveal] = useState<{ rootId: string; nonce: number } | null>(null)
  const [leaving, setLeaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  /*
   * veltica.com monta el modo diseño oculto antes de que se pulse el botón
   * (precarga) y, al abrirlo, dice dónde se estaba leyendo: la página va ahí y
   * la barra vuelve a entrar deslizándose (`opened` como clave).
   */
  const [goTo, setGoTo] = useState<{ fraction: number; nonce: number } | null>(null)
  const [opened, setOpened] = useState(0)
  /*
   * Cerrar la barra no deshace el diseño: la barra se recoge, la página se
   * queda como se dejó a todo lo ancho, y el botón de veltica.com vuelve a
   * salir para reabrirla (`veltica:demo-collapsed`). Sus enlaces llevan de
   * verdad a su destino (`veltica:demo-navigate`).
   */
  const [collapsed, setCollapsed] = useState(false)
  function collapse() {
    setCollapsed(true)
    window.parent.postMessage({ type: 'veltica:demo-collapsed' }, '*')
  }
  useEffect(() => {
    if (!SPLIT) return
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; y?: unknown } | null
      if (event.source !== window.parent) return
      /* Reabrir la barra recogida: la página sigue donde está. */
      if (data?.type === 'veltica:demo-expand') {
        setCollapsed(false)
        setOpened((count) => count + 1)
        return
      }
      if (data?.type !== 'veltica:demo-open') return
      const fraction = typeof data.y === 'number' ? Math.min(1, Math.max(0, data.y)) : 0
      setGoTo((last) => ({ fraction, nonce: (last?.nonce ?? 0) + 1 }))
      setCollapsed(false)
      setOpened((count) => count + 1)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])
  const railRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let alive = true
    setFailed(false)
    const fromDemo = () => getDemoPage(siteLanguage).then((demo) => {
      if (!alive) return
      if (!demo.slots.length) { setFailed(true); return }
      setForm(hydrateFromDemo(demo, { homeName: t('pages.home'), recipe: comboRecipe(START_STYLE) }))
    })
    /* La portada de veltica.com; si no hay una conectada, la de la demo. */
    const load = FROM_SITE
      ? getDemoSite().then((site) => {
          if (!alive) return
          const loaded = hydrateFromSite(site, t('pages.home'))
          /* De veltica.com con un diseño hecho: no hay nada que enseñar aquí,
             se guarda y se sigue a registrarse (o al panel), donde el asistente
             lo abre. Ver `demoDesign`. */
          if (SITE_DESIGN && saveDemoDesign(withSiteDesign(loaded, SITE_DESIGN, site.sections))) {
            window.location.replace(hasSession ? '/' : '/signup')
            return
          }
          setForm(SITE_DESIGN ? withSiteDesign(loaded, SITE_DESIGN, site.sections) : loaded)
        }, fromDemo)
      : fromDemo()
    void load.catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
    // `t` cambia con el idioma, que ya está en `siteLanguage`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteLanguage, attempt])

  /* La pastilla del raíl, medida como en el asistente. */
  useLayoutEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const place = () => {
      const active = rail.querySelector<HTMLButtonElement>('button.is-current')
      if (!active) return
      rail.style.setProperty('--step-x', `${active.offsetLeft}px`)
      rail.style.setProperty('--step-w', `${active.offsetWidth}px`)
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(rail)
    return () => observer.disconnect()
  }, [step, form])

  const patch = (next: Partial<CreateForm>) => setForm((current) => (current ? { ...current, ...next } : current))
  /* Lo escrito en la vista previa con doble clic, como en el asistente. */
  const writeText = (elementId: string, text: string) => setForm((current) => (current ? { ...current, textEdits: { ...current.textEdits, [elementId]: text } } : current))

  function selectRecipe(recipe: CreateForm['recipe']) {
    if (!form) return
    patch({ recipe, theme: themeForRecipe(form, recipe) })
  }

  /* Lo mismo que «Combinación» en el asistente: la combinación entera, y sin
     id, la de partida. */
  function selectCombination(styleId: string | null) {
    if (!form) return
    const recipe = styleId
      ? recipeWithBrand(comboRecipe(styleId), brandPaletteOf(comboRecipe(styleId)))
      : form.aiRecipe ?? form.recipe
    const brandPalette = recipe.palette ? { ...recipe.palette } : brandPaletteOf(recipe)
    patch({ combination: styleId ?? undefined, recipe, brandPalette, theme: themeForRecipe(form, recipe) })
  }

  function start() {
    if (!form || leaving) return
    if (!saveDemoDesign(form)) { setSaveFailed(true); return }
    setLeaving(true)
    /* Con sesión, al panel, que abre el asistente con el diseño; sin ella, a
       registrarse, y el panel lo abre al entrar. Desde la ventana flotante, en
       la pestaña entera: el registro no cabe en una ventana dentro de otra. */
    const destination = new URL(hasSession ? '/' : '/signup', window.location.origin).toString()
    if (EMBEDDED) {
      try { window.top!.location.href = destination } catch { window.open(destination, '_blank', 'noopener') }
      return
    }
    window.location.assign(destination)
  }

  /* Sin variantes de sección (la portada de veltica.com), sin ese paso. */
  const steps = form && !form.sections.length ? STEPS.filter((item) => item.key !== 'sections') : STEPS
  const stepIndex = steps.findIndex((item) => item.key === step)
  const recipeProps = form ? { recipe: form.recipe, theme: form.theme as unknown as ThemeProjectTheme, onChange: selectRecipe } : null

  const stepContent = (
    <>
      {failed ? (
        <div className="demo-failed" role="alert">
          <IconAlertCircle size={18} aria-hidden="true" />
          <span>{t('demo.loadFailed')}</span>
          <button type="button" className="dash-secondary-btn" onClick={() => setAttempt((value) => value + 1)}>{t('app.retry')}</button>
        </div>
      ) : null}

      {form && recipeProps ? (<>
        {step === 'combination' ? <CombinationStep
          value={form.combination ?? null}
          aiTheme={(form.aiRecipe ? themeForRecipe(form, form.aiRecipe) : form.theme) as unknown as ThemeProjectTheme}
          aiStyleId={(form.aiRecipe ?? form.recipe).designStyleId}
          baseTheme={defaultTheme}
          onChange={selectCombination}
          origin={{ badge: t('demo.combination.startBadge'), hint: t('demo.combination.startHint') }}
        /> : null}
        {step === 'colors' ? <ColorsStep
          {...recipeProps}
          brand={form.brandPalette}
          aiPalette={null}
          aiPaletteId={null}
          preset={null}
          onBrandChange={(next, paletteId) => {
            const recipe = recipeWithBrand(paletteId ? { ...form.recipe, paletteId } : form.recipe, next)
            patch({ brandPalette: next, recipe, theme: themeForRecipe(form, recipe) })
          }}
        /> : null}
        {step === 'fonts' ? <FontsStep {...recipeProps} /> : null}
        {step === 'style' ? <StylePresetsStep {...recipeProps} comboStyleId={form.combination ?? form.aiRecipe?.designStyleId} /> : null}
        {step === 'motion' ? <MotionStep
          value={siteMotionOf(form)}
          suggested={suggestedMotion(form)}
          theme={form.theme as unknown as ThemeProjectTheme}
          onChange={(motionStyle) => { patch({ motionStyle }); setMotionReplay((value) => value + 1) }}
        /> : null}
        {step === 'sections' ? <WizardSectionsStep
          loadVariant={async (id, lang) => (await getDemoBlock(id, lang)).elements}
          form={form}
          onChange={setForm}
          activeRootId={previewSection}
          onReveal={(rootId) => setReveal((last) => ({ rootId, nonce: (last?.nonce ?? 0) + 1 }))}
        /> : null}
      </>) : !failed ? (
        <div className="cwa-wz-preview-wait"><IconLoader2 className="dash-spin" size={20} /></div>
      ) : null}
    </>
  )

  const preview = form
    ? <PreviewLive
        form={form}
        focus="top"
        reveal={reveal}
        onSection={setPreviewSection}
        page={0}
        onPage={() => {}}
        onText={writeText}
        motionReplay={motionReplay}
        {...(SPLIT ? {
          minDesignWidth: 0,
          initialScroll: START_SCROLL,
          goTo,
          onShown: announceShown,
          onLink: (href: string) => window.parent.postMessage({ type: 'veltica:demo-navigate', href }, '*'),
        } : {})}
      />
    : <div className="cwa-wz-preview-wait"><IconLoader2 className="dash-spin" size={20} /></div>

  /*
   * El modo diseño de veltica.com: la página a su tamaño, con una barra a la
   * izquierda. Se abre encima de la página real (`designLauncher`) y arranca en
   * el punto donde se leía, así que se lee como la misma página que hace sitio
   * a la barra, no como una ventana nueva.
   */
  if (SPLIT) {
    return (
      <div className={`demo-split${collapsed ? ' is-collapsed' : ''}`} role="main" aria-label={t('demo.aria')}>
        <aside className="demo-split-panel" key={opened} aria-label={t('demo.stepsAria')} aria-hidden={collapsed || undefined}>
          <header className="demo-split-head">
            <span className="demo-split-title">
              <span className="demo-split-swatch" aria-hidden="true" />
              {t('demo.split.title')}
            </span>
            <button type="button" className="demo-split-close" onClick={collapse} aria-label={t('common.close')} title={t('common.close')}>
              <IconX size={16} />
            </button>
          </header>
          <nav className="demo-split-tabs">
            {steps.map(({ key, labelKey, Icon }) => (
              <button
                type="button"
                key={key}
                className={key === step ? 'is-current' : ''}
                aria-pressed={key === step}
                disabled={!form}
                onClick={() => setStep(key)}
              >
                <Icon size={15} aria-hidden="true" />
                <span>{t(labelKey)}</span>
              </button>
            ))}
          </nav>
          <div className="demo-split-scroll cwa-wz-scroll">
            <section className="dash-svc-panel-step demo-split-step" key={step}>
              <h2>{t(`cwa.wz.${step}.title`)}</h2>
              {stepContent}
            </section>
          </div>
          <footer className="demo-split-foot">
            {saveFailed ? <p className="dash-svc-error" role="alert">{t('demo.saveFailed')}</p> : null}
            <button type="button" className="dash-primary-btn demo-split-start" disabled={!form || leaving} onClick={start}>
              {leaving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCheck size={14} />}
              {t('demo.start')}
            </button>
            <small>{t('demo.previewNote')}</small>
          </footer>
        </aside>
        <div className="demo-split-page">{preview}</div>
      </div>
    )
  }

  return (
    <div className={`dash-svc-wizard cwa-wz demo-page${EMBEDDED ? ' is-embedded' : ''}`} role="main" aria-label={t('demo.aria')}>
      <WizardTopBar
        onBack={leave}
        onClose={leave}
        closeHidden
        center={(
          <nav className="dash-svc-wizard-progress" aria-label={t('demo.stepsAria')}>
            <div className="dash-svc-wizard-rail" ref={railRef}>
              <span className="dash-svc-wizard-rail-thumb" aria-hidden="true" />
              {steps.map(({ key, labelKey, Icon }, index) => (
                <button
                  type="button"
                  key={key}
                  className={index === stepIndex ? 'is-current' : ''}
                  disabled={!form}
                  aria-current={index === stepIndex ? 'step' : undefined}
                  onClick={() => setStep(key)}
                >
                  <span><Icon size={14} /></span>
                  <em>{t(labelKey)}</em>
                </button>
              ))}
            </div>
          </nav>
        )}
        actions={(
          <button type="button" className="dash-primary-btn demo-start" disabled={!form || leaving} onClick={start}>
            {leaving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCheck size={14} />}
            {t('demo.start')}
          </button>
        )}
      />

      <div className="dash-svc-wizard-body cwa-wz-body">
        <div className="dash-svc-wizard-form cwa-wz-form">
          <div className="cwa-wz-scroll">
            <div className="dash-svc-wizard-form-inner">
              <section className="dash-svc-panel-step" key={step}>
                <span className="dash-svc-step-kicker">{t('demo.kicker')}</span>
                <h2>{t(`cwa.wz.${step}.title`)}</h2>
                <p>{t(`cwa.wz.${step}.lead`)}</p>

                {stepContent}
              </section>
              {saveFailed ? <p className="dash-svc-error" role="alert">{t('demo.saveFailed')}</p> : null}
            </div>
          </div>

          <div className="cwa-wz-footer">
            <div className="dash-svc-step-actions">
              {stepIndex > 0
                ? <button type="button" className="dash-secondary-btn" onClick={() => setStep(steps[stepIndex - 1]!.key)}><IconArrowLeft size={14} /> {t('cwa.back')}</button>
                : <span />}
              {stepIndex < steps.length - 1 ? (
                <button type="button" className="dash-secondary-btn" disabled={!form} onClick={() => setStep(steps[stepIndex + 1]!.key)}>
                  {t('cwa.next')} <IconArrowRight size={14} />
                </button>
              ) : (
                <button type="button" className="dash-primary-btn" disabled={!form || leaving} onClick={start}>
                  {leaving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCheck size={14} />}
                  {t('demo.start')}
                </button>
              )}
            </div>
          </div>
        </div>

        <aside className="dash-svc-wizard-preview cwa-wz-preview" aria-label={t('createAi.previewTitle')}>
          <div className="dash-svc-preview-heading">
            <span>{t('createAi.previewTitle')}</span>
            <em>{t('demo.previewNote')}</em>
          </div>
          <div className="dash-svc-browser cwa-wz-browser">
            <div className="dash-svc-browser-bar">
              <i /><i /><i />
              <span className="dash-svc-browser-url">{t('demo.address')}</span>
            </div>
            {preview}
          </div>
        </aside>
      </div>
    </div>
  )
}
